import { FC } from 'react';

import { PublicFeedEvent } from '@shared/types/event';

import { useEventQuery } from '@queries/EventQueries';

import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { EventHeader, EventImage } from '@components/cards/EventCard';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Popup } from '@components/layout/Popup/Popup';

import { useEventRsvpActions } from '@screens/event/EventRsvpScreen/hooks/useEventRsvpActions';

interface EventRsvpPopupContentProps {
  event: PublicFeedEvent;
  onClose: () => void;
}

const EventRsvpPopupExternalLinkButton: FC<{ event: PublicFeedEvent }> = ({
  event: publicFeedEvent,
}) => {
  const { event } = useEventQuery(publicFeedEvent._id);

  if (event?.externalLinkButton?.url) {
    return (
      <Button
        className="mb-3"
        roundness="rounded"
        onClick={() => openExternalUrl(event.externalLinkButton.url)}
      >
        {event.externalLinkButton.text ?? 'Get Tickets'}
      </Button>
    );
  }

  return null;
};

interface EventRsvpPopupProps {
  isVisible: boolean;
  event: PublicFeedEvent | undefined;
  onClose: () => void;
}

const PopupContent: FC<EventRsvpPopupContentProps> = ({ event }) => {
  const { rsvp, isUpdatingRsvp, handleMarkAttending, handleMarkInterested } = useEventRsvpActions({
    eventId: event._id,
    eventTitle: event.title,
  });

  const isCurrentUserAttending = rsvp?.rsvp === 'yes';
  const isCurrentUserInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

  return (
    <>
      <EventImage event={event} />
      <EventHeader event={event} subtitleDirection="column" hideRsvp />
      <EventRsvpPopupExternalLinkButton event={event} />

      <div className="d-flex gap-2 w-100">
        <div className="w-50">
          <Button
            noWrap
            roundness="rounded"
            disabled={isUpdatingRsvp}
            variant={isCurrentUserAttending ? 'default' : 'outlined'}
            startIcon={
              <FontAwesomeIcon
                faStyle="far"
                name={isCurrentUserAttending ? 'check-circle' : 'circle'}
              />
            }
            onClick={handleMarkAttending}
          >
            Attending
          </Button>
        </div>
        <div className="w-50">
          <Button
            noWrap
            roundness="rounded"
            disabled={isUpdatingRsvp}
            color="neutral"
            variant={isCurrentUserInterested ? 'default' : 'outlined'}
            startIcon={
              <FontAwesomeIcon
                faStyle="far"
                name={isCurrentUserInterested ? 'check-circle' : 'circle'}
              />
            }
            onClick={handleMarkInterested}
          >
            Interested
          </Button>
        </div>
      </div>
    </>
  );
};

export const EventRsvpPopup: FC<EventRsvpPopupProps> = ({ isVisible, event, onClose }) => {
  return (
    <Popup height="full" isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        {isVisible && event && <PopupContent event={event} onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};
